import { useEffect, useMemo, useState } from "react";
import { X, Minus, Plus, ShoppingBag, Check, Tag } from "lucide-react";
import { useCart } from "../context/CartContext";
import ProductImage from "./ProductImage";
import {
  PACK_SIZES,
  PACK_DISCOUNTS,
  calcPackBase,
  calcPackPrice,
  calcPackSavings,
} from "../lib/pricing";

/**
 * Pack builder dialog. Lets the user pick a pack size (4/8/12) and choose how
 * many of each flavour go into the pack. Adds the pack as a SINGLE cart item
 * with a category-level bulk discount applied.
 */
const PackBuilder = ({
  open,
  onClose,
  title,
  products,
  unitPrice,
  category,
  categoryLabel,
  initialSize = 4,
}) => {
  const { addItem } = useCart();
  const [size, setSize] = useState(initialSize);
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (open) {
      setSize(initialSize);
      setSelections({});
    }
  }, [open, initialSize]);

  const selectedCount = useMemo(
    () => Object.values(selections).reduce((s, n) => s + n, 0),
    [selections]
  );
  const remaining = size - selectedCount;
  const isFull = selectedCount === size;

  const basePrice = calcPackBase(size, unitPrice);
  const packPrice = calcPackPrice(size, unitPrice);
  const savings = calcPackSavings(size, unitPrice);
  const discountPct = Math.round((PACK_DISCOUNTS[size] || 0) * 100);

  const adjust = (productId, delta) => {
    setSelections((prev) => {
      const current = prev[productId] || 0;
      const next = current + delta;
      if (next < 0) return prev;
      const others = Object.entries(prev)
        .filter(([id]) => id !== productId)
        .reduce((s, [, v]) => s + v, 0);
      if (others + next > size) return prev;
      const updated = { ...prev, [productId]: next };
      if (updated[productId] === 0) delete updated[productId];
      return updated;
    });
  };

  const handleAdd = () => {
    if (!isFull) return;
    const components = Object.entries(selections).map(([id, qty]) => {
      const p = products.find((x) => x.id === id);
      return { product_id: id, name: p?.name || id, quantity: qty };
    });
    const packItem = {
      id: `pack-${category}-${size}-${Date.now()}`,
      name: `${size}-Pack ${categoryLabel}`,
      price: packPrice,
      image: products[0]?.image || "",
      pack: {
        category,
        size,
        discountPercent: discountPct,
        unitPrice,
        basePrice,
        components,
      },
    };
    addItem(packItem, 1);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      data-testid="pack-builder"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
    >
      <div
        data-testid="pack-builder-overlay"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-3xl max-h-[88vh] glass rounded-3xl border border-[#ff1a8c]/40 shadow-[0_0_60px_rgba(255,26,140,0.35)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-6 lg:p-8 border-b border-white/10">
          <div>
            <p className="text-[0.7rem] uppercase tracking-[0.3em] text-[#00b4ff] mb-2">
              Pick your pack
            </p>
            <h2 className="font-script text-3xl sm:text-4xl text-white">{title}</h2>
            <p className="text-sm text-gray-400 mt-2">
              Mix and match {categoryLabel} flavours — ${unitPrice.toFixed(2)} each. Bulk discount
              applied automatically.
            </p>
          </div>
          <button
            data-testid="pack-builder-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#ff1a8c]"
            aria-label="Close pack builder"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Size picker */}
        <div className="px-6 lg:px-8 pt-6">
          <p className="text-[0.7rem] uppercase tracking-[0.25em] text-gray-400 mb-3">Pack size</p>
          <div data-testid="pack-size-selector" className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3">
            {PACK_SIZES.map((s) => {
              const sPrice = calcPackPrice(s, unitPrice);
              const sDisc = Math.round((PACK_DISCOUNTS[s] || 0) * 100);
              return (
                <button
                  key={s}
                  data-testid={`pack-size-${s}`}
                  onClick={() => {
                    setSize(s);
                    setSelections({});
                  }}
                  className={`flex flex-col items-center sm:items-start gap-0.5 sm:gap-1 px-2 py-2.5 sm:px-5 sm:py-3 rounded-xl sm:rounded-2xl border transition-all text-center sm:text-left ${
                    size === s
                      ? "bg-[#ff1a8c] border-[#ff1a8c] text-white shadow-[0_0_18px_rgba(255,26,140,0.6)]"
                      : "border-white/15 text-gray-200 hover:border-[#00b4ff]"
                  }`}
                >
                  <span className="text-[11px] sm:text-sm uppercase tracking-[0.14em] sm:tracking-[0.18em] font-semibold">
                    {s}-Pack
                  </span>
                  <span className="text-sm sm:text-base font-bold">${sPrice.toFixed(2)}</span>
                  <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.18em] ${size === s ? "text-white/80" : "text-[#00b4ff]"}`}>
                    Save {sDisc}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Flavour grid */}
        <div className="flex-1 overflow-y-auto px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[0.7rem] uppercase tracking-[0.25em] text-gray-400">
              Choose flavours
            </p>
            <p
              data-testid="pack-progress"
              className={`text-sm font-semibold ${isFull ? "text-[#00b4ff]" : "text-[#ff1a8c]"}`}
            >
              {selectedCount} / {size} selected
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {products.map((p) => {
              const qty = selections[p.id] || 0;
              const soldOut = !!p.sold_out;
              const atMax = soldOut || (remaining === 0 && qty === 0);
              return (
                <li
                  key={p.id}
                  data-testid={`pack-flavour-${p.id}`}
                  className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${
                    soldOut
                      ? "border-white/5 opacity-45"
                      : qty > 0
                      ? "border-[#00b4ff]/60 bg-[#00b4ff]/5 shadow-[0_0_18px_rgba(0,180,255,0.18)]"
                      : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <ProductImage
                    src={p.image}
                    alt={p.name}
                    size="sm"
                    className="w-14 h-14 rounded-xl object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                    {soldOut ? (
                      <p data-testid={`pack-sold-out-${p.id}`} className="text-xs uppercase tracking-[0.18em] text-[#ff1a8c] font-semibold">Sold out</p>
                    ) : (
                      <p className="text-xs text-gray-400">${unitPrice.toFixed(2)} each</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid={`pack-decrement-${p.id}`}
                      onClick={() => adjust(p.id, -1)}
                      disabled={qty === 0}
                      className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center hover:border-[#ff1a8c] disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label={`Remove ${p.name}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span
                      data-testid={`pack-qty-${p.id}`}
                      className="text-sm w-6 text-center font-semibold"
                    >
                      {qty}
                    </span>
                    <button
                      data-testid={`pack-increment-${p.id}`}
                      onClick={() => adjust(p.id, 1)}
                      disabled={atMax}
                      className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center hover:border-[#00b4ff] disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label={`Add ${p.name}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div className="p-6 lg:p-8 border-t border-white/10 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[0.7rem] uppercase tracking-[0.25em] text-gray-400">Pack total</p>
            <div className="flex items-baseline gap-3 flex-wrap">
              <p data-testid="pack-total" className="text-2xl font-bold neon-blue">
                ${packPrice.toFixed(2)}
              </p>
              {discountPct > 0 && (
                <>
                  <span className="text-sm text-gray-500 line-through">
                    ${basePrice.toFixed(2)}
                  </span>
                  <span
                    data-testid="pack-savings"
                    className="inline-flex items-center gap-1 text-[0.7rem] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-[#00b4ff]/15 text-[#00b4ff] border border-[#00b4ff]/40"
                  >
                    <Tag className="w-3 h-3" /> Save ${savings.toFixed(2)} ({discountPct}%)
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            data-testid="pack-add-to-cart"
            onClick={handleAdd}
            disabled={!isFull}
            className="neon-btn"
          >
            {isFull ? (
              <>
                <Check className="w-4 h-4" /> Add Pack to Cart
              </>
            ) : (
              <>
                <ShoppingBag className="w-4 h-4" /> Pick {remaining} more
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackBuilder;
